import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { ICallPanel } from "../models";
import { api } from "../services/api";

interface IPanelContext {
  isLoading: boolean;
  isRegistered: () => boolean;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  setIsConnected: (value: boolean) => void;
  isConnected: boolean;
  panel: ICallPanel | null;
  handleRegisterPanel: (panel: ICallPanel | null) => Promise<boolean>;
  handleClosePanel: () => Promise<void>;
}

interface IPanelProvider {
  children: ReactNode;
}

const TIME_LIMIT = 120;
const TIME_INITIAL = 1;

export const PanelContext = createContext<IPanelContext>({} as IPanelContext);

export const PanelProvider: React.FC<IPanelProvider> = ({ children }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [panel, setPanel] = useState<ICallPanel | null>(null);
  const [clock, setClock] = useState<number>(TIME_INITIAL);

  const handleRegisterPanel = async (panel: ICallPanel | null) => {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        setIsLoading(true);

        localStorage.setItem("panel@drclick", JSON.stringify(panel));
        await getPanel();
        setIsOpen(true);
        resolve(true);
      } catch (error: any) {
        setIsLoading(false);
        if (error.response) {
          reject(error.response.data.error.message);
        }
      }
    });
  };

  const handleClosePanel = async () => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        setIsLoading(true);

        localStorage.removeItem("panel@drclick");
        window.location.reload();
        resolve();
      } catch (error: any) {
        setIsLoading(false);
      }
    });
  };

  const getPanel = async () => {
    setIsLoading(true);
    const panelStorage = localStorage.getItem("panel@drclick");
    if (panelStorage !== null) {
      try {
        const res = await api.get(
          `/users-patients/call-panel-by-id?idpainelchamado=${
            JSON.parse(panelStorage).idpainelchamado
          }`
        );
        setPanel(res.data.data);
        setIsLoading(false);
        setIsConnected(true);
      } catch (error: any) {
        if (error.response.data.status === 404) {
          localStorage.clear();
        }
        setIsLoading(false);
        setIsConnected(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const validateConnection = async () => {
    const panelStorage = localStorage.getItem("panel@drclick");
    if (panelStorage !== null) {
      try {
        await api.get(
          `/users-patients/call-panel-by-id?idpainelchamado=${
            JSON.parse(panelStorage).idpainelchamado
          }`
        );
        setIsConnected(true);
        window.location.reload();
      } catch (error: any) {
        if (error.response.data.status === 404) {
          localStorage.clear();
        }
        setIsConnected(false);
      }
    } else {
      setIsConnected(false);
    }
  };

  const isRegistered = () => {
    const totemStorage = localStorage.getItem("panel@drclick");
    return totemStorage === null ? false : true;
  };

  useEffect(() => {
    getPanel();
  }, []);

  useEffect(() => {
    let time: any = null;
    if (panel) {
      if (clock < TIME_LIMIT) {
        time = setInterval(() => {
          setClock((state) => state + 1);
        }, 1000);
      } else {
        setClock(TIME_INITIAL);
        validateConnection();
      }

      return () => clearInterval(time);
    }
  }, [clock, panel]);

  return (
    <PanelContext.Provider
      value={{
        isLoading,
        panel,
        isOpen,
        setIsOpen,
        isConnected,
        setIsConnected,
        isRegistered,
        handleRegisterPanel,
        handleClosePanel,
      }}
    >
      {children}
    </PanelContext.Provider>
  );
};

export const usePanel = () => {
  return useContext(PanelContext);
};
